// §6b — THE CONVENTION-SURFACE BANNED-OUTPUT ADDITIONS (R7 spec §4, "contract-tested on R7 surfaces").
//
// WHY THESE ARE A SEPARATE, SURFACE-SCOPED LAYER AND NOT NEW MEMBERS OF `BANNED_TERMS`.
//
// The R7 additions are ordinary English that the REST of the product legitimately says. "Missing" appears in
// ledger-side language, "match" appears in "CaRMS match cycle" and in the R-1 vocabulary the product is built
// around, "fit" appears in any CV describing a fitness programme, "priority" and "focus on" appear in applicant
// text. Adding them to the global literal list would fail the build on the product's own copy — which §12.6 part
// 1b says is exactly as serious a failure as a miss. What §4 actually bans is this VOCABULARY ON THIS SURFACE:
// the §6b panel must never turn a training-standard area into a gap, a priority, a program requirement, or a
// score. So the layer is scoped to the convention surface, where every string is system-authored and the whole
// point is that none of it may drift into selection language.
//
// THE ALLOWLIST IS THE SAME MECHANISM AS §12.6 PART 2 — keyed by COPY-STRING ID, never by free text. The five
// mandatory disclaimers and the three empty strings are the only place "requirement", "weight", "prediction" and
// "select" may appear, and they appear there in NEGATED form by ratification.
//
// APPLICANT TEXT IS NEVER SCANNED (§12.6 part 0). Nothing on this surface is applicant text: the area labels, the
// recover questions and the provenance strings are all ratified registry content.
import { CONVENTION_ALLOWLISTED_COPY_IDS } from './rubricCopy';
import { scanSystemText, type BanScanResult } from './bannedOutputRegistry';

/**
 * The §4 additions, as patterns.
 *
 * EVERY TERM §4 NAMES IS BANNED BARE, on word boundaries, case-insensitively (Sol round 2, blocker 3). The
 * previous shape banned "fit" and "match" only in a hand-enumerated VERDICT context ("a good fit for", "your
 * match"), on the reasoning that a future ratified string might want the bare noun. That reasoning had it exactly
 * backwards. §4 does not ban a list of phrasings, it bans a VOCABULARY on this surface, and a context-gated
 * pattern is a contract test that passes on every phrasing nobody thought of — "a strong specialty match", "how
 * well your CV fits", "areas that match" all rendered clean. On a surface where every string is system-authored
 * and ratified, the cost of the bare ban is a copy review, and the cost of the gated one is selection language
 * shipping under a green test. So the bare word is the rule and the ALLOWLIST is the escape hatch — the same
 * mechanism §12.6 part 2 uses, keyed by copy-string id, never by free text.
 *
 * NOTHING ON THE SURFACE NEEDS THE ESCAPE HATCH TODAY, and that is asserted rather than assumed: no registered
 * `CS-CONV-*` / `CS-SECTION-CONV*` string, no `plain_language` area label, no `recover_question`, and no
 * provenance field in any ratified signal set contains one of these terms (T-CONV-6's false-positive suite).
 *
 * The ONE pattern that stays contextual is the aggregate, and for a structural reason rather than a lexical one:
 * "{n} of {N} areas" is banned as a CROSS-ROW AGGREGATE, while `CS-CONV-CORPUS-NOTE`'s "{n} of {N} institutions'
 * published descriptions" is a per-row prevalence disclosure. The pattern therefore names the counted noun
 * explicitly (areas/criteria/signals/standards/domains) — it is not a word that is banned, it is a count over the
 * rows themselves.
 */
export const CONVENTION_BANNED_PATTERNS: readonly { id: string; re: RegExp }[] = [
  // Deficiency framing — an undocumented area is "not documented here", never a deficit.
  { id: 'r7/gap', re: /\bgaps?\b/i },
  { id: 'r7/missing', re: /\bmissing\b/i },
  { id: 'r7/deficit', re: /\bdeficits?\b/i },
  { id: 'r7/deficient', re: /\bdeficient\b/i },
  { id: 'r7/weakness', re: /\bweakness(es)?\b/i },
  { id: 'r7/lacking', re: /\black(s|ing)\b/i },
  // Prioritisation — §0: no ordering by importance, prevalence or documentation status.
  { id: 'r7/priority', re: /\bpriorit(y|ies|ise|ize|ised|ized|isation|ization)\b/i },
  { id: 'r7/focus-on', re: /\bfocus\s+on\b/i },
  { id: 'r7/most-important', re: /\bmost\s+important\b/i },
  { id: 'r7/key-area', re: /\bkey\s+areas?\b/i },
  // Selection claims — the sources are training standards, not selection documents.
  { id: 'r7/programs-look-for', re: /\bprograms?\s+(look\s+for|want|value|expect|require|prefer)\b/i },
  { id: 'r7/expected-by-programs', re: /\b(expected|required)\s+by\s+programs?\b/i },
  { id: 'r7/selection-criterion', re: /\bselection\s+criteri(on|a)\b/i },
  // Fit / alignment / match — BARE, in every inflection §4's vocabulary reaches.
  { id: 'r7/alignment', re: /\balign(ment|ments|ed|s|ing)?\b/i },
  { id: 'r7/fit', re: /\bfit(s|ted|ting|ness)?\b/i },
  { id: 'r7/match', re: /\bmatch(es|ed|ing)?\b/i },
  // Any cross-row aggregate over the areas themselves.
  { id: 'r7/n-of-n-areas', re: /\b\d+\s+(of|out\s+of)\s+\d+\s+(areas?|criteri(on|a)|signals?|standards?|domains?)\b/i },
];

/**
 * Scan one system-authored string that renders on the §6b surface.
 *
 * Runs the GLOBAL registry first (an id allowlisted here is still held to §12 unless it is also allowlisted
 * there — the two allowlists are separate on purpose: §12.6 part 2 governs the product-wide bans, this one
 * governs the R7 additions), then the surface-scoped additions.
 */
export function scanConventionText(text: string, copyId?: string): BanScanResult {
  const global = scanSystemText(text, copyId);
  if (!global.ok) return global;
  if (copyId !== undefined && (CONVENTION_ALLOWLISTED_COPY_IDS as readonly string[]).includes(copyId)) {
    return { ok: true };
  }
  for (const { id, re } of CONVENTION_BANNED_PATTERNS) {
    const m = re.exec(text);
    if (m) return { ok: false, term: m[0], pattern_id: id };
  }
  return { ok: true };
}
